import type {
  CompatibilityNotice,
  PlaylistDocument,
  PlaylistFormat,
  ProTVTarget,
  ValidationIssue,
} from "./types";
import { isHttpUrl } from "./url";

export function getCompatibilityNotices(
  document: PlaylistDocument,
  target: ProTVTarget,
  format: PlaylistFormat,
): CompatibilityNotice[] {
  const notices: CompatibilityNotice[] = [];

  if (target !== "3.x" && format === "json") {
    notices.push({
      field: "format",
      count: document.entries.length,
      message: `JSON playlists are a ProTV 3.x format. ${target} export will use tokenized text.`,
    });
  }

  if (target === "2.2") {
    const alternateCount = document.entries.filter((entry) => entry.alternateUrl).length;
    const tagCount = document.entries.filter((entry) => entry.tags.length > 0).length;
    if (alternateCount > 0) {
      notices.push({ field: "alternateUrl", count: alternateCount, message: "Alternate URLs are not available in ProTV 2.2." });
    }
    if (tagCount > 0) {
      notices.push({ field: "tags", count: tagCount, message: "Tags were added in ProTV 2.3 and will be omitted." });
    }
  }

  if (target !== "3.x") {
    const descriptionCount = document.entries.filter((entry) => entry.description).length;
    if (descriptionCount > 0) {
      notices.push({
        field: "description",
        count: descriptionCount,
        message: `Descriptions use the ProTV 3.x token model and will be omitted from ${target}.`,
      });
    }
  }

  return notices;
}

export function validatePlaylist(document: PlaylistDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const seenUrls = new Map<string, string>();

  for (const entry of document.entries) {
    if (!isHttpUrl(entry.mainUrl)) {
      issues.push({ entryId: entry.id, level: "error", message: "Main URL must begin with http:// or https://." });
    }

    if (entry.alternateUrl && !isHttpUrl(entry.alternateUrl)) {
      issues.push({ entryId: entry.id, level: "error", message: "Alternate URL must begin with http:// or https://." });
    }

    if (!entry.title) {
      issues.push({ entryId: entry.id, level: "warning", message: "No title is set; ProTV may display the URL instead." });
    }

    if (entry.imagePath && (/^https?:\/\//i.test(entry.imagePath) || entry.imagePath.startsWith("/"))) {
      issues.push({
        entryId: entry.id,
        level: "warning",
        message: "Image paths must be relative to the Unity project root, such as Assets/ProTV/PlaylistImages/example.jpg.",
      });
    }

    const duplicateOf = seenUrls.get(entry.mainUrl);
    if (duplicateOf) {
      issues.push({ entryId: entry.id, level: "warning", message: "Duplicate main URL." });
    } else if (entry.mainUrl) {
      seenUrls.set(entry.mainUrl, entry.id);
    }
  }

  return issues;
}

export function deduplicateEntries(document: PlaylistDocument): { document: PlaylistDocument; removed: number } {
  const seen = new Set<string>();
  const entries = document.entries.filter((entry) => {
    const key = entry.mainUrl.trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return { document: { ...document, entries }, removed: document.entries.length - entries.length };
}
